import { h } from "preact";
import { useEffect, useState } from "preact/hooks";

import type { Token } from "prism";

import { getPrismInstance } from "../../../apps/getPrismInstance";
import classname from "../../../foundation/utils/classname";

import styles from "./Code.module.css";

// TODO: Get the types from "prism" module
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Prism = any;

type Line = string | Token;

function groupTokensByLine(tokens: Line[]) {
    let group: Line[] = [];
    const groups: Line[][] = [group];

    for (const token of tokens) {
        if (typeof token === "string" && token.includes("\n")) {
            const lines = token.split("\n");

            group.push(lines[0]);

            for (let i = 1; i < lines.length - 1; i++) {
                groups.push([lines[i]]);
            }

            group = [lines[lines.length - 1]];
            groups.push(group);
        } else {
            group.push(token);
        }
    }

    return groups;
}

function getLines(content: string, lang: string | undefined, prism: Prism) {
    if (!lang) {
        return content.split("\n");
    }

    if (!prism.languages[lang]) {
        return content.split("\n");
    }

    return groupTokensByLine(prism.tokenizeFlat(content, prism.languages[lang]));
}

interface Props {
    class?: string;
    style?: string;
    content: string;
    lang?: string;
    showLineIndicator?: boolean;
    textSelectable?: boolean;
}

function Code({
    class: cssClass,
    style,
    content,
    lang,
    showLineIndicator,
    textSelectable = true
}: Props) {
    const [lines, setLines] = useState<Line[][] | string[]>([]);
    const [prism, setPrism] = useState<Prism | null>(null);

    useEffect(() => {
        if (prism === null) {
            getPrismInstance().then(setPrism);
        }
    }, [content, lang]);

    useEffect(() => {
        if (!prism) {
            return;
        }

        function highlight() {
            const updatedLines = getLines(content, lang, prism);
            setLines(updatedLines);
        }

        highlight();
    }, [content, lang, prism]);

    function renderToken(token: Token | string, index: number) {
        if (typeof token === "string") {
            if (token === "") {
                return null;
            }

            return <span key={index}>{token}</span>;
        }

        return (
            <span key={index} class={classname("token", token.type)}>
                {token.content}
            </span>
        );
    }

    function renderLine(line: Line[] | string) {
        if (Array.isArray(line)) {
            return line.map(renderToken);
        }

        return line;
    }

    function renderLines() {
        return lines.map((line, index) => (
            <span key={index} class={styles.lineWrapper}>
                <span class={styles.lineno}></span>
                <pre class={classname(styles.line, {
                    [styles.noSelect]: !textSelectable
                })}>
                    {renderLine(line)}
                </pre>
            </span>
        ));
    }

    return (
        <pre class={classname(styles.code, cssClass, {
            [styles.showLineIndicator]: showLineIndicator
        })}>
            <code class={`language-${lang}`} style={style}>
                {renderLines()}
            </code>
        </pre>
    );
}

export default Code;
export {
    Props as CodeProps
};

